import type { Movimiento } from "../api/tipos.ts";
import { pesos } from "../formato.ts";

/**
 * Los ejes en los que dos lados se encuentran — o no.
 *
 * El motor ya publica en qué se apoyó (`senales`), y hasta ahora eso se
 * mostraba como una tira de insignias debajo de un veredicto. Aquí se convierte
 * en la ESTRUCTURA de la pantalla: cada eje es una línea entre el banco y tu
 * registro, que cierra o no cierra.
 *
 * Vive aparte de React porque es la única parte que puede equivocarse en
 * silencio: qué eje cierra, y con qué cifra.
 */

export type Cierre =
  /** Los dos lados coinciden en este eje. */
  | "cierra"
  /** Coinciden a medias, y el hueco tiene tamaño. */
  | "hueco"
  /** Nadie pudo comprobarlo. No es un fallo: es que no se sabe. */
  | "ausente";

export interface Eje {
  clave: "nombre" | "monto" | "fecha" | "factura";
  etiqueta: string;
  cierre: Cierre;
  /**
   * Cómo quedó, en palabras y siempre visible: «exacto», «faltan $15.000».
   * Antes solo se leía al pasar el ratón, y en el celular eso no existe; con
   * el color como única pista, la columna no se entendía (30-sep-2026).
   */
  detalle: string;
}

/**
 * Los ejes de un movimiento, en el orden en que pesan.
 *
 * El nombre va primero porque es lo que más puntúa y lo que un humano
 * comprueba primero; la factura va última porque hoy casi nunca está verificada
 * y encabezar con una ausencia sería empezar por lo que no se sabe.
 */
export function ejesDe(m: Movimiento): Eje[] {
  const s = m.senales;
  if (!s) return [];

  const ejes: Eje[] = [];

  // Sin grado, nadie comparó el nombre. Con grado NULO —o NINGUNO, que es como
  // lo dice la propuesta desde factura— sí se comparó y NO se parece: eso es un
  // dato que no cuadra, no uno sin comprobar. Pintarlo en gris hacía que el
  // resumen dijera «coinciden todos» con el nombre en contra (30-sep-2026).
  const grado = s.nombre;
  ejes.push({
    clave: "nombre",
    etiqueta: "Nombre",
    cierre: !grado ? "ausente" : grado === "TOTAL" || grado === "FUERTE" ? "cierra" : "hueco",
    detalle: grado ? (GRADO_NOMBRE_CORTO[grado] ?? "no se parece") : "sin comparar",
  });

  if (s.montoExacto || s.monto === "EXACTO") {
    ejes.push({ clave: "monto", etiqueta: "Monto", cierre: "cierra", detalle: "exacto" });
  } else if (s.diferenciaCentavos) {
    ejes.push({
      clave: "monto",
      etiqueta: "Monto",
      // Dentro de la tolerancia el eje cierra: es lo que el cliente configuró
      // como "esto para mí es lo mismo". Fuera de ella, el hueco es un hueco.
      cierre: s.montoEnTolerancia ? "cierra" : "hueco",
      detalle: s.montoEnTolerancia
        ? `${diferenciaEnPalabras(m, s.diferenciaCentavos)}, dentro de tu tolerancia`
        : diferenciaEnPalabras(m, s.diferenciaCentavos),
    });
  } else {
    ejes.push({ clave: "monto", etiqueta: "Monto", cierre: "ausente", detalle: "sin comparar" });
  }

  // Sin la señal, nadie comparó la fecha: es lo que pasa con la propuesta que
  // nace de una factura, que no tiene fecha de pago contra la que medir. Antes
  // salía «lejos», un ⚠ que nadie había comprobado (SPEC-029, prueba 8).
  ejes.push({
    clave: "fecha",
    etiqueta: "Fecha",
    cierre: s.fechaCercana === undefined ? "ausente" : s.fechaCercana ? "cierra" : "hueco",
    detalle:
      s.fechaCercana === undefined
        ? "sin comparar"
        : s.fechaCercana
          ? "cerca de la del registro"
          : "lejos de la del registro",
  });

  ejes.push({
    clave: "factura",
    etiqueta: "Factura",
    cierre: s.facturaVerificada ? "cierra" : "ausente",
    detalle: s.facturaVerificada ? "está en tu cartera" : "no se pudo comprobar",
  });

  return ejes;
}

/** El grado del nombre, para leerse detrás de la palabra «Nombre». */
const GRADO_NOMBRE_CORTO: Record<string, string> = {
  TOTAL: "idéntico",
  FUERTE: "muy parecido",
  DEBIL: "apenas parecido",
  NULO: "no se parece",
};

/**
 * La diferencia de monto, dicha hacia el lado en que falta la plata.
 *
 * `diferenciaCentavos` es absoluta, así que el sentido hay que reconstruirlo
 * comparando los dos lados. Decir siempre «falta» era mentir la mitad de las
 * veces: un pago que entra DE MÁS es un caso distinto —y más incómodo— que uno
 * que entra corto. Sin el otro lado no se inventa un sentido.
 */
function diferenciaEnPalabras(m: Movimiento, diferencia: number): string {
  const esperado = m.senales?.esperadoCentavos;
  const entro = m.montoCentavos;
  if (esperado === undefined || entro === null || esperado === entro) return `difiere ${pesos(diferencia)}`;
  return entro < esperado ? `faltan ${pesos(diferencia)}` : `sobran ${pesos(diferencia)}`;
}

/** Cuántos ejes cierran de los que se pudieron evaluar. Es lo que se resume en una línea. */
export function cuentaCierres(ejes: Eje[]): { cierran: number; evaluables: number } {
  const evaluables = ejes.filter((e) => e.cierre !== "ausente");
  return { cierran: evaluables.filter((e) => e.cierre === "cierra").length, evaluables: evaluables.length };
}

/**
 * El resumen en una frase, sin la palabra «ejes», que era nuestra y no del
 * usuario. Lo que no se pudo comprobar se dice aparte: no es un fallo.
 */
export function resumenDeCoincidencias(ejes: Eje[]): string {
  if (!ejes.length) return "";
  const { cierran, evaluables } = cuentaCierres(ejes);
  const sinComprobar = ejes.length - evaluables;
  const coinciden =
    evaluables === 0
      ? "No se pudo comprobar ningún dato."
      : cierran === evaluables
        ? evaluables === 1
          ? "Coincide el único dato que se pudo comprobar."
          : `Coinciden los ${evaluables} datos${sinComprobar ? " que se pudieron comprobar" : ""}.`
        : `Coinciden ${cierran} de ${evaluables} datos${sinComprobar ? " comprobables" : ""}.`;
  return coinciden;
}

/**
 * Los dos lados del encuentro.
 *
 * `registro` es null cuando nadie propuso nada: entró plata y no hay nada
 * enfrente. Ese caso NO es un error ni un estado degradado — es la mitad del
 * trabajo del producto, y la interfaz tiene que poder dibujarlo vacío.
 */
export interface Lados {
  banco: {
    montoCentavos: number | null;
    quien: string;
    /** Dónde consignaron, cuando el banco lo dice y no dice quién pagó. */
    sucursal?: string;
    llave?: string;
    fecha?: string | null;
  };
  registro: {
    montoCentavos: number | null;
    cliente: string;
    factura?: string;
  } | null;
}

export function ladosDe(m: Movimiento): Lados {
  const hayPropuesta = (m.abonosClaves?.length ?? 0) > 0;

  return {
    banco: {
      montoCentavos: m.montoCentavos,
      // Sin relleno: si el banco no dijo quién pagó, esto va vacío y la tarjeta
      // decide qué poner. Con el relleno, el componente tenía que comparar
      // contra esa cadena para saber si había nombre — preguntarle al texto algo
      // que sabe el dato.
      quien: m.contraparte ?? "",
      sucursal: m.sucursal || undefined,
      llave: m.llave || undefined,
      fecha: m.fecha,
    },
    registro: hayPropuesta
      ? {
          // `esperadoCentavos` es la suma de los abonos propuestos. Si el
          // backend es viejo y no la manda, se cae al monto del banco: enseñar
          // el mismo número a los dos lados es menos malo que enseñar uno
          // inventado.
          montoCentavos: m.senales?.esperadoCentavos ?? m.montoCentavos,
          cliente: m.cliente ?? "—",
          factura: m.factura,
        }
      : null,
  };
}
